<template>
  <v-main>
    <v-app-bar flat class="nav" height="68">

      <!-- ─────────── LOGO (RTL: right side) ─────────── -->
      <v-toolbar-title class="nav__logo">
        <v-img alt="Logo" :src="logo ? logo : require('@/assets/logo.svg')" contain transition="scale-transition"
          max-height="46" max-width="200" />
      </v-toolbar-title>

      <v-spacer></v-spacer>

      <!-- ─────────── DESKTOP NAV (centered) ─────────── -->
      <nav class="nav__list" v-if="$vuetify.breakpoint.mdAndUp" aria-label="ניווט ראשי">
        <button v-for="(item, index) in menu" :key="item.id" v-if="item.show" type="button" class="nav__pill"
          :class="{ 'nav__pill--active': $route.path === item.link }" @click="selectedItFunc(index, item.link)">
          <span class="nav__pill-label">{{ item.title }}</span>
          <span v-if="item.id === 'tickets' && badgeCount > 0" class="nav__pill-badge">{{ badgeCount }}</span>
        </button>
      </nav>

      <v-spacer></v-spacer>

      <!-- ─────────── DESKTOP USER ─────────── -->
      <div class="nav__user" v-if="$vuetify.breakpoint.mdAndUp">
        <v-menu offset-y left transition="slide-y-transition" :close-on-content-click="true" :nudge-bottom="6"
          content-class="nav__menu-wrap">
          <template v-slot:activator="{ on, attrs }">
            <button type="button" class="nav__user-btn" v-bind="attrs" v-on="on" aria-label="תפריט משתמש">
              <span class="nav__avatar" aria-hidden="true">{{ userName.charAt(0) }}</span>
              <span class="nav__user-info">
                <span class="nav__user-name">{{ userName }}</span>
                <span class="nav__user-role">{{ roleLabel }}</span>
              </span>
              <svg class="nav__chev" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
          </template>

          <!-- User dropdown menu -->
          <div class="nav__menu">
            <div class="nav__menu-head">
              <div class="nav__menu-avatar">{{ userName.charAt(0) }}</div>
              <div class="nav__menu-info">
                <span class="nav__menu-name">{{ userName }}</span>
                <span class="nav__menu-email" :class="{ 'nav__menu-email--link': role === 'admin' }"
                  @click.stop="showLogs()" :title="role === 'admin' ? 'לחץ לצפייה בלוגים' : ''">{{ email }}</span>
                <span class="nav__menu-role-pill">{{ roleLabel }}</span>
              </div>
            </div>

            <div class="nav__menu-items">
              <button v-if="isAdmin" type="button" class="nav__menu-item" @click="swichToAdmin()">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
                  stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="17 1 21 5 17 9" />
                  <path d="M3 11V9a4 4 0 0 1 4-4h14" />
                  <polyline points="7 23 3 19 7 15" />
                  <path d="M21 13v2a4 4 0 0 1-4 4H3" />
                </svg>
                <span>חזור לניהול</span>
              </button>
              <button v-if="isAgent" type="button" class="nav__menu-item" @click="swichToAgent()">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
                  stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="17 1 21 5 17 9" />
                  <path d="M3 11V9a4 4 0 0 1 4-4h14" />
                  <polyline points="7 23 3 19 7 15" />
                  <path d="M21 13v2a4 4 0 0 1-4 4H3" />
                </svg>
                <span>חזור לסוכן</span>
              </button>
              <button v-if="role === 'admin'" type="button" class="nav__menu-item" @click="showLogs()">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
                  stroke-linecap="round" stroke-linejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                </svg>
                <span>צפייה בלוגים</span>
              </button>
              <div class="nav__menu-sep" v-if="isAdmin || isAgent || role === 'admin'"></div>
              <button type="button" class="nav__menu-item nav__menu-item--danger" @click="logOut()">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
                  stroke-linecap="round" stroke-linejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
                <span>התנתק</span>
              </button>
            </div>
          </div>
        </v-menu>
      </div>

      <!-- ─────────── MOBILE BURGER ─────────── -->
      <div v-if="$vuetify.breakpoint.smAndDown">
        <v-menu bottom left offset-y transition="slide-y-transition" :close-on-content-click="true" :nudge-bottom="8"
          content-class="nav__menu-wrap">
          <template v-slot:activator="{ on, attrs }">
            <button type="button" class="nav__burger" v-bind="attrs" v-on="on" aria-label="תפריט">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
                stroke-linecap="round" stroke-linejoin="round">
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
              <span v-if="badgeCount > 0" class="nav__burger-dot" aria-hidden="true"></span>
            </button>
          </template>

          <div class="nav__mobile">
            <div class="nav__mobile-head">
              <div class="nav__menu-avatar">{{ userName.charAt(0) }}</div>
              <div class="nav__menu-info">
                <span class="nav__menu-name">{{ userName }}</span>
                <span class="nav__menu-email" :class="{ 'nav__menu-email--link': role === 'admin' }"
                  @click.stop="showLogs()">{{ email }}</span>
                <span class="nav__menu-role-pill">{{ roleLabel }}</span>
              </div>
            </div>

            <div class="nav__mobile-list">
              <button v-for="item in menu" :key="item.id" v-if="item.show" type="button" class="nav__mobile-item"
                :class="{ 'nav__mobile-item--active': $route.path === item.link }"
                @click="selectedItFunc(null, item.link)">
                <v-icon class="nav__mobile-icon" small>{{ getIconForMenuItem(item.id) }}</v-icon>
                <span class="nav__mobile-title">{{ item.title }}</span>
                <span v-if="item.id === 'tickets' && badgeCount > 0" class="nav__mobile-badge">{{ badgeCount }}</span>
              </button>
            </div>

            <div class="nav__mobile-actions">
              <button v-if="isAdmin" type="button" class="nav__menu-item" @click="swichToAdmin()">
                <v-icon small>mdi-swap-horizontal</v-icon>
                <span>חזור לניהול</span>
              </button>
              <button v-if="isAgent" type="button" class="nav__menu-item" @click="swichToAgent()">
                <v-icon small>mdi-swap-horizontal</v-icon>
                <span>חזור לסוכן</span>
              </button>
              <button type="button" class="nav__menu-item nav__menu-item--danger" @click="logOut()">
                <v-icon small>mdi-logout</v-icon>
                <span>התנתק</span>
              </button>
            </div>
          </div>
        </v-menu>
      </div>
    </v-app-bar>

    <!-- ─────────── NOTIFICATION SNACKBAR ─────────── -->
    <v-snackbar :timeout="5000" :value="alert" :multi-line="true" max-width="420" left shaped color="#0d2c6d"
      class="nav__alert">
      <div class="d-flex align-center">
        <div class="nav__alert-icon" aria-hidden="true">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
            stroke-linecap="round" stroke-linejoin="round">
            <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
            <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
          </svg>
        </div>
        <span class="nav__alert-text">{{ alertText }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn text color="#fdb721" v-bind="attrs" @click="alert = false">סגור</v-btn>
      </template>
    </v-snackbar>
  </v-main>
</template>

<script>
import Auth from '@/services/auth.service.js'
import ApiServices from "@/services/api.service";
import { mapState } from 'vuex';

export default {
  props: {
    balanceChanged: Number,
  },

  data() {
    return {
      menu: [],
      sellerMenu: [
        { id: "home", title: "סטטיסטיקות", link: "/home", show: true },
        { id: "customers", title: "לקוחות", link: "/customers", show: true },
        { id: "all_manuim", title: "מנויים", link: "/all-manuim", show: true },
        { id: "create_manui", title: "הקמת מנוי", link: "/create-manui", show: true },
        { id: "sims", title: "סימים", link: "/sims", show: true },
        // { id: "tickets", title: "פניות", link: "/tickets", show: true },
      ],
      adminMenu: [
        { id: "home", title: "סטטיסטיקות", link: "/home", show: true },
        { id: "customers", title: "לקוחות", link: "/customers", show: true },
        { id: "all_manuim", title: "מנויים", link: "/all-manuim", show: true },
        { id: "users", title: "משתמשים", link: "/users", show: true },
        { id: "plans", title: "חבילות", link: "/plans", show: true },
        { id: "communities", title: "קהילות", link: "/communities", show: true },
        { id: "sims", title: "סימים", link: "/sims", show: true },
        { id: "ServiceRequests", title: "קריאות שירות", link: "/service-requests", show: true },
        // { id: "tickets", title: "פניות", link: "/tickets", show: true },
        { id: "reports", title: "דוחות", link: "/reports", show: true },
      ],
      agentMenu: [
        { id: "home", title: "סטטיסטיקות", link: "/home", show: true },
        { id: "customers", title: "לקוחות", link: "/customers", show: true },
        { id: "all_manuim", title: "מנויים", link: "/all-manuim", show: true },
        { id: "users", title: "משתמשים", link: "/users", show: true },
      ],
      userName: "",
      email: "",
      role: "",
      alert: false,
      alertColorBt: "green",
      alertText: "",
      isFocused: false,
      isAdmin: false,
      isAgent: false,
      logo: null,
      badgeCount: 0,
    }
  },
  methods: {
    async seLogo() {
      try {
        if (localStorage.getItem("logo") !== "not found") {
          let token = localStorage.getItem("token");
          let api = process.env.VUE_APP_BASE_URL + `/users/get_logo`;
          const res = await fetch(api, ApiServices.requestOptions("GET", "", token));
          const jsonObject = await res.json();
          if (res.status >= 400) {
          } else if (res.status === 200) {
            if (jsonObject) {
              this.logo = process.env.VUE_APP_BASE_URL + jsonObject;
              localStorage.setItem("logo", this.logo);
            } else {
              localStorage.setItem("logo", "not found");
            }
          }
        }
      } catch (error) {
        console.log(error);
      }
    },
    logOut() {
      localStorage.removeItem("token");
      localStorage.removeItem("logo");
      this.$router.replace({ name: "login" })
    },
    selectedItFunc(index, link) {
      if (this.$route.path !== link) {
        this.$router.replace({ path: link })
      }
    },
    setNavListIItems() {
      this.userName = Auth.getUserName();
      this.email = Auth.getUserEmail();
      this.role = Auth.getUserRole();
      if (this.role === "admin") {
        this.menu = this.adminMenu;
      } else if (this.role === "agent") {
        this.menu = this.agentMenu;
      } else {
        this.menu = this.sellerMenu;
      }
      if (localStorage.getItem("tokenAdmin")) {
        this.isAdmin = true;
      } else if (localStorage.getItem("tokenAgent")) {
        this.isAgent = true;
      }
    },
    swichToAdmin() {
      localStorage.setItem("token", localStorage.getItem("tokenAdmin"));
      localStorage.removeItem("tokenAdmin");
      localStorage.removeItem("logo");
      this.isAdmin = false;
      if (localStorage.getItem("tokenAgent")) {
        localStorage.removeItem("tokenAgent");
      }
      this.$store.commit('setRenderNav');
      this.$router.replace({ name: 'users' })
    },
    swichToAgent() {
      localStorage.setItem("token", localStorage.getItem("tokenAgent"));
      localStorage.removeItem("tokenAgent");
      localStorage.removeItem("logo");
      this.isAgent = false;
      this.$store.commit('setRenderNav');
      this.$router.replace({ name: 'users' })
    },
    showAlert(message, color) {
      this.alertText = message;
      this.alertColorBt = color;
      this.alert = true
      setTimeout(() => {
        this.alert = false
      }, 5000);
    },
    showLogs() {
      if (this.role === "admin") {
        this.$router.push({ name: 'logs' })
      }
    },
    showReplyTicketAlert(data) {
      this.badgeCount += 1
      this.$store.commit('setReplyTicket', { reply: data.newResponse, ticketId: data.ticketId });
      this.showAlert("יש לך תגובה חדשה בפנייה: " + data.subject, "green");
    },
    showNewTicketAlert(data) {
      this.badgeCount += 1
      this.$store.commit('setNewTicket', data.ticketCreated);
      this.showAlert("פנייה חדשה נפתחה בנושא: " + data.subject, "green");
    },
    getIconForMenuItem(id) {
      const icons = {
        home: "mdi-view-dashboard",
        customers: "mdi-account-group",
        all_manuim: "mdi-cellphone",
        create_manui: "mdi-plus-circle",
        sims: "mdi-sim",
        tickets: "mdi-ticket-account",
        users: "mdi-account-cog",
        plans: "mdi-package-variant",
        communities: "mdi-account-group-outline",
        reports: "mdi-file-chart"
      };
      return icons[id] || "mdi-menu";
    }
  },
  computed: {
    ...mapState({
      rerenderNav: state => state.rerenderNav
    }),
    roleLabel() {
      if (this.role === 'admin') return 'מנהל';
      if (this.role === 'agent') return 'סוכן';
      return 'משווק';
    }
  },
  watch: {
    $route(to, from) {
      if (to.fullPath === "/tickets") {
        this.badgeCount = 0
      }
    },
    rerenderNav(newValue, oldValue) {
      this.setNavListIItems();
    }
  },
  created() {
    this.setNavListIItems();

    this.seLogo();

    if (this.$socket) {
      this.$socket.emit('joinRoom', { token: localStorage.getItem('token') });
      this.$bus.$on('newUserReply', this.showReplyTicketAlert);
      this.$bus.$on('newAdminReply', this.showReplyTicketAlert);
      this.$bus.$on('newUserTicket', this.showNewTicketAlert);
      this.$bus.$on('newAdminTicket', this.showNewTicketAlert);
    }
  },
  beforeDestroy() {
    this.$bus.$off('newUserReply', null);
    this.$bus.$off('newAdminReply', null);
    this.$bus.$off('newUserTicket', null);
    this.$bus.$off('newAdminTicket', null);
  }
}
</script>

<style scoped>
/* ─────────── TOKENS ─────────── */
.nav {
  --c-navy: #0d2c6d;
  --c-navy-deep: #081f54;
  --c-indigo: #3F51B5;
  --c-purple: #8066f0;
  --c-gold: #fdb721;
  --c-surface: #ffffff;
  --c-bg: #f3f6f9;
  --c-border: #e3e8ef;
  --c-border-soft: #eef2f7;
  --c-text: #0f172a;
  --c-text-soft: #475569;
  --c-text-mute: #94a3b8;

  --shadow-sm: 0 1px 2px rgba(13, 44, 109, 0.04), 0 1px 3px rgba(13, 44, 109, 0.06);
  --shadow-lg: 0 18px 40px -12px rgba(13, 44, 109, 0.20), 0 8px 16px -8px rgba(13, 44, 109, 0.10);
  --ease: cubic-bezier(0.22, 1, 0.36, 1);

  font-family: 'Assistant', 'Heebo', system-ui, sans-serif;
}

/* ─────────── APP BAR ─────────── */
.nav.v-app-bar {
  background: var(--c-surface) !important;
  border-bottom: 1px solid var(--c-border-soft);
  box-shadow: 0 1px 0 rgba(13, 44, 109, 0.04), 0 6px 24px -16px rgba(13, 44, 109, 0.12) !important;
  padding: 0 12px;
}

/* ─────────── LOGO ─────────── */
.nav__logo {
  padding: 0 16px;
  display: flex;
  align-items: center;
  transition: transform 0.3s var(--ease);
}

.nav__logo:hover {
  transform: scale(1.03);
}

/* ─────────── DESKTOP NAV PILLS ─────────── */
.nav__list {
  display: flex;
  align-items: center;
  gap: 4px;
  height: 100%;
}

.nav__pill {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 38px;
  padding: 0 18px;
  border: none;
  background: transparent;
  border-radius: 999px;
  color: var(--c-text-soft);
  font-family: inherit;
  font-size: 1.10rem;
  font-weight: 600;
  cursor: pointer;
  position: relative;
  transition: all 0.22s var(--ease);
  white-space: nowrap;
}

.nav__pill:hover {
  background: rgba(13, 44, 109, 0.06);
  color: var(--c-navy);
}

.nav__pill--active {
  background: var(--c-navy);
  color: #fff;
  box-shadow: 0 6px 14px -4px rgba(13, 44, 109, 0.45);
}

.nav__pill--active:hover {
  background: var(--c-navy-deep);
  color: #fff;
}

.nav__pill-label {
  line-height: 1;
}

.nav__pill-badge {
  display: inline-grid;
  place-items: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 999px;
  background: var(--c-gold);
  color: #2d1f00;
  font-size: 0.7rem;
  font-weight: 800;
  font-feature-settings: "tnum" 1;
  box-shadow: 0 2px 6px -2px rgba(253, 183, 33, 0.65);
  animation: nav-bump 0.5s var(--ease);
}

.nav__pill--active .nav__pill-badge {
  box-shadow: 0 0 0 2px var(--c-navy), 0 2px 6px -2px rgba(253, 183, 33, 0.65);
}

@keyframes nav-bump {
  0% {
    transform: scale(0.5);
    opacity: 0;
  }

  60% {
    transform: scale(1.15);
    opacity: 1;
  }

  100% {
    transform: scale(1);
    opacity: 1;
  }
}

/* ─────────── USER BUTTON ─────────── */
.nav__user {
  display: flex;
  align-items: center;
  padding-left: 6px;
}

.nav__user-btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 4px 12px 4px 6px;
  border: 1px solid var(--c-border);
  border-radius: 999px;
  background: var(--c-surface);
  cursor: pointer;
  font-family: inherit;
  transition: all 0.2s var(--ease);
}

.nav__user-btn:hover {
  border-color: rgba(13, 44, 109, 0.22);
  background: #fafbfd;
}

.nav__avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: linear-gradient(135deg, var(--c-navy) 0%, var(--c-indigo) 100%);
  color: #fff;
  font-weight: 800;
  font-size: 0.95rem;
  letter-spacing: 0.02em;
  flex-shrink: 0;
  box-shadow: 0 2px 8px -2px rgba(13, 44, 109, 0.35);
  user-select: none;
}

.nav__user-info {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 1px;
  min-width: 0;
}

.nav__user-name {
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--c-text);
  line-height: 1.1;
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.nav__user-role {
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--c-text-mute);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  line-height: 1;
}

.nav__chev {
  color: var(--c-text-mute);
  transition: transform 0.25s var(--ease);
  flex-shrink: 0;
}

.nav__user-btn[aria-expanded="true"] .nav__chev {
  transform: rotate(180deg);
}

/* ─────────── DROPDOWN MENU (rendered by v-menu in body) ─────────── */
/* needs ::v-deep wrapper because v-menu teleports the content outside this component's DOM */
::v-deep .nav__menu-wrap {
  border-radius: 14px !important;
  box-shadow: var(--shadow-lg) !important;
  background: transparent !important;
  overflow: visible !important;
}

.nav__menu {
  min-width: 280px;
  background: var(--c-surface);
  border-radius: 14px;
  border: 1px solid var(--c-border-soft);
  overflow: hidden;
  font-family: 'Assistant', 'Heebo', system-ui, sans-serif;
  direction: rtl;
  background-color: white;
}

.nav__menu-head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 18px;
  background:
    radial-gradient(500px 120px at 90% -40%, rgba(253, 183, 33, 0.10), transparent 60%),
    linear-gradient(135deg, #0d2c6d 0%, #142f78 100%);
  color: #fff;
}

.nav__menu-avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.22);
  color: #fff;
  font-weight: 800;
  font-size: 1.05rem;
  flex-shrink: 0;
}

.nav__menu-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.nav__menu-name {
  font-size: 0.98rem;
  font-weight: 800;
  color: #fff;
  letter-spacing: -0.005em;
}

.nav__menu-email {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.72);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 220px;
}

.nav__menu-email--link {
  cursor: pointer;
  transition: color 0.2s var(--ease);
}

.nav__menu-email--link:hover {
  color: var(--c-gold);
  text-decoration: underline;
}

.nav__menu-role-pill {
  display: inline-block;
  width: fit-content;
  margin-top: 4px;
  padding: 2px 10px;
  border-radius: 999px;
  background: rgba(253, 183, 33, 0.18);
  border: 1px solid rgba(253, 183, 33, 0.32);
  color: var(--c-gold);
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.nav__menu-items {
  padding: 6px;
  background: var(--c-surface);
}

.nav__menu-item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 12px;
  border: none;
  border-radius: 9px;
  background: transparent;
  color: var(--c-text);
  font-family: inherit;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.18s var(--ease), color 0.18s var(--ease);
  text-align: right;
}

.nav__menu-item:hover {
  background: rgba(13, 44, 109, 0.06);
  color: var(--c-navy);
}

.nav__menu-item svg {
  color: var(--c-text-mute);
  transition: color 0.18s var(--ease);
  flex-shrink: 0;
}

.nav__menu-item:hover svg {
  color: var(--c-navy);
}

.nav__menu-item--danger {
  color: #b91c1c;
}

.nav__menu-item--danger:hover {
  background: rgba(185, 28, 28, 0.07);
  color: #991b1b;
}

.nav__menu-item--danger svg {
  color: #b91c1c;
}

.nav__menu-item--danger:hover svg {
  color: #991b1b;
}

.nav__menu-sep {
  height: 1px;
  margin: 6px 8px;
  background: var(--c-border-soft);
}

/* ─────────── MOBILE BURGER ─────────── */
.nav__burger {
  position: relative;
  width: 42px;
  height: 42px;
  border-radius: 11px;
  border: 1px solid var(--c-border);
  background: var(--c-surface);
  color: var(--c-navy);
  cursor: pointer;
  display: grid;
  place-items: center;
  transition: all 0.2s var(--ease);
  margin-left: 6px;
}

.nav__burger:hover {
  background: #fafbfd;
  border-color: rgba(13, 44, 109, 0.22);
}

.nav__burger-dot {
  position: absolute;
  top: 6px;
  left: 6px;
  /* RTL: dot on top-left of burger */
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: var(--c-gold);
  box-shadow: 0 0 0 2px var(--c-surface), 0 0 0 3px rgba(253, 183, 33, 0.4);
  animation: nav-pulse 1.8s ease-in-out infinite;
}

@keyframes nav-pulse {

  0%,
  100% {
    box-shadow: 0 0 0 2px var(--c-surface), 0 0 0 3px rgba(253, 183, 33, 0.4);
  }

  50% {
    box-shadow: 0 0 0 2px var(--c-surface), 0 0 0 6px rgba(253, 183, 33, 0.10);
  }
}

/* ─────────── MOBILE MENU CONTENT ─────────── */
.nav__mobile {
  min-width: 280px;
  max-width: 320px;
  background: var(--c-surface);
  border-radius: 14px;
  border: 1px solid var(--c-border-soft);
  overflow: hidden;
  font-family: 'Assistant', 'Heebo', system-ui, sans-serif;
  direction: rtl;
}

.nav__mobile-head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background:
    radial-gradient(400px 100px at 90% -40%, rgba(253, 183, 33, 0.10), transparent 60%),
    linear-gradient(135deg, #0d2c6d 0%, #142f78 100%);
  color: #fff;
}

.nav__mobile-list {
  padding: 6px;
  background: var(--c-surface);
}

.nav__mobile-item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 11px 12px;
  border: none;
  border-radius: 9px;
  background: transparent;
  color: var(--c-text-soft);
  font-family: inherit;
  font-size: 0.92rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.18s var(--ease);
  text-align: right;
  position: relative;
}

.nav__mobile-item:hover {
  background: rgba(13, 44, 109, 0.05);
  color: var(--c-navy);
}

.nav__mobile-item--active {
  background: rgba(13, 44, 109, 0.08);
  color: var(--c-navy);
  font-weight: 700;
}

.nav__mobile-item--active::before {
  content: '';
  position: absolute;
  top: 8px;
  bottom: 8px;
  right: 0;
  width: 3px;
  border-radius: 2px;
  background: var(--c-navy);
}

.nav__mobile-icon {
  color: inherit !important;
  flex-shrink: 0;
}

.nav__mobile-title {
  flex: 1;
}

.nav__mobile-badge {
  display: inline-grid;
  place-items: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 999px;
  background: var(--c-gold);
  color: #2d1f00;
  font-size: 0.7rem;
  font-weight: 800;
  font-feature-settings: "tnum" 1;
}

.nav__mobile-actions {
  border-top: 1px solid var(--c-border-soft);
  padding: 6px;
  background: var(--c-surface);
}

/* ─────────── ALERT SNACKBAR ─────────── */
.nav__alert ::v-deep .v-snack__wrapper {
  border-radius: 14px !important;
  box-shadow: 0 20px 40px -12px rgba(13, 44, 109, 0.5) !important;
  border: 1px solid rgba(253, 183, 33, 0.25);
}

.nav__alert-icon {
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  border-radius: 9px;
  background: rgba(253, 183, 33, 0.18);
  color: var(--c-gold);
  margin-left: 12px;
  flex-shrink: 0;
}

.nav__alert-text {
  font-family: inherit;
  font-size: 0.92rem;
  font-weight: 600;
  color: #fff;
  line-height: 1.4;
}

/* ─────────── RESPONSIVE ─────────── */
@media (max-width: 960px) {
  .nav.v-app-bar {
    height: 60px !important;
    padding: 0 6px;
  }

  .nav__logo {
    padding: 0 8px;
  }
}

@media (prefers-reduced-motion: reduce) {

  .nav__pill-badge,
  .nav__burger-dot {
    animation: none;
  }
}
</style>